import { useMemo } from 'react';
import { useQueryClient } from 'react-query';
import { useGetBlueprintPowerProfile } from 'features/blueprints/hooks/useGetBlueprintPowerProfile';
import { ManualPowerProfile } from 'features/blueprints/hooks/useUpdateBlueprint/types';
import { PowerProfileMode, powerProfileModes } from 'greener-ui-library';
import { useAxiosQuery } from 'hooks/useAxiosQuery';
import { BLUEPRINT_POWER_PROFILE_CACHE_KEY, EQUIPMENT_RECOMMENDATION_CACHE_KEY } from 'services/queryKeys';

export interface RecommendedSetup {
  batteryMinKw: number;
  batteryMinKwh: number;
  gensetMinKva: number;
}

interface UseGetEquipmentRecommendationsParams {
  gridSizeAmperes?: number;
  gridSizeKw?: number;
  powerProfile?: string;
  blueprintId?: number; // For fetching power profile from API in edit mode
  mode?: 'create' | 'edit'; // Mode to determine if we should fetch from API
  powerProfileMode?: PowerProfileMode;
  manualPowerProfileData?: ManualPowerProfile;
  kitePowerScenario?: string | null;
}

export const useGetEquipmentRecommendations = ({
  gridSizeAmperes,
  gridSizeKw,
  powerProfile,
  blueprintId,
  mode = 'create',
  powerProfileMode,
  manualPowerProfileData,
  kitePowerScenario,
}: UseGetEquipmentRecommendationsParams) => {
  const queryClient = useQueryClient();
  const isManualMode = powerProfileMode === powerProfileModes.manual;
  const shouldGetPowerProfile = mode === 'edit' && !!blueprintId && !powerProfile;
  const hasManualProfileData = isManualMode && !!manualPowerProfileData;

  const { isLoading: isPowerProfileLoading } = useGetBlueprintPowerProfile({
    blueprintId: blueprintId?.toString() || '',
    enabled: shouldGetPowerProfile,
  });

  // Get raw CSV from react-query cache if available
  const powerProfileCsvFromApi = queryClient.getQueryData<string>(
    BLUEPRINT_POWER_PROFILE_CACHE_KEY(blueprintId?.toString() || '')
  );

  // Use powerProfile from form if available, otherwise use API data in edit mode
  const finalPowerProfile = powerProfile || (mode === 'edit' ? powerProfileCsvFromApi : undefined);

  const formData = useMemo(() => {
    if (hasManualProfileData || !finalPowerProfile) {
      return null;
    }
    const formDataToSend = new FormData();
    const csvBlob = new Blob([finalPowerProfile], { type: 'text/csv' });
    formDataToSend.append('power_profile', csvBlob, 'power-profile.csv');
    if (kitePowerScenario) {
      formDataToSend.append('kite_power_scenario', kitePowerScenario);
    }
    return formDataToSend;
  }, [hasManualProfileData, finalPowerProfile, kitePowerScenario]);

  const gridSizeAmperesValue = gridSizeAmperes ?? 0;
  const gridSizeKwValue = gridSizeKw ?? 0;

  const fileEndpointQueryParams = new URLSearchParams({
    ...(gridSizeAmperesValue ? { 'grid-size-amperes': gridSizeAmperesValue.toString() } : {}),
    ...(gridSizeKwValue ? { 'grid-size-kw': gridSizeKwValue.toString() } : {}),
  }).toString();

  const endpoint = hasManualProfileData
    ? '/blueprints/recommendation/manual'
    : `/blueprints/recommendation/file?${fileEndpointQueryParams}`;

  const body = hasManualProfileData
    ? {
        power_profile_generation_data: manualPowerProfileData,
        ...(kitePowerScenario ? { kite_power_scenario: kitePowerScenario } : {}),
        ...(gridSizeKwValue ? { grid_size_kw: gridSizeKwValue } : {}),
        ...(gridSizeAmperesValue ? { grid_size_amperes: gridSizeAmperesValue } : {}),
      }
    : formData;

  const isEnabled = hasManualProfileData || !!finalPowerProfile;

  const {
    data,
    isLoading: isRecommendationsLoading,
    ...rest
  } = useAxiosQuery<RecommendedSetup>(
    EQUIPMENT_RECOMMENDATION_CACHE_KEY({
      mode: powerProfileMode,
      gridSizeAmperes: gridSizeAmperesValue,
      gridSizeKw: gridSizeKwValue,
      kitePowerScenario: kitePowerScenario ?? '',
      powerProfileData: hasManualProfileData ? JSON.stringify(manualPowerProfileData) : finalPowerProfile,
    }),
    endpoint,
    {
      method: 'POST',
      body,
      enabled: isEnabled,
    }
  );

  return {
    data,
    isLoading: isRecommendationsLoading || isPowerProfileLoading,
    ...rest,
  };
};
